/**
 * A slicer, as a tile: placed, moved and sized on the canvas like a chart, and
 * narrowing the charts on its tab - Power BI's slicer for a dashboard.
 *
 * It wears Explore's slicer row (`.slicer-row`: name, track, readout, action),
 * so a range here is the same control as under an Explore chart. Four kinds:
 *
 * * **list** - the row as a heading, every value with a checkbox beneath.
 * * **dropdown** - the row with a dropdown in place of the track.
 * * **range** - the row, with a histogram from `/columns/profile`.
 * * **time** - the same over a time column, read as dates.
 *
 * Nothing re-queries while a viewer is still choosing: a list offers Apply once
 * its ticks differ from what is applied, and a range commits when let go. What
 * is ticked belongs to the viewer (`slicer-state.ts`), never the shared
 * document; what the slicer *is* - kind, column, label - is edited in the
 * filter dialog (✎ in Edit mode). Explore's Play is not offered: every step
 * would refetch every chart on the tab.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { formatNum, fmtTimeShort } from '../plot/render/format.ts';
import { activeDashboard, dashboards, type DashboardTile } from './dashboard-state.ts';
import type { ColumnProfile } from '../../api/endpoints.ts';
import type { SharedContext } from './tile-spec.ts';
import {
  applySelection,
  narrows,
  selectionOf,
  slicerOptions,
  slicerProfile,
  slicerSelections,
  slicerWindowValues,
  slicerNotes,
  slicerNoteOf,
  dismissSlicerNote,
  type SlicerOptions,
  type SlicerSelection,
} from './slicer-state.ts';
import type { DashDropdown } from './dash-dropdown.ts';

/** Show a filter box in a list once it is longer than this. */
const SEARCH_ABOVE = 8;
/** A window never closes to nothing; this is its narrowest, as a share of the range. */
const MIN_SHARE = 0.004;
/** How often a drag commits when filtering while dragging: about four times a second. */
const LIVE_COMMIT_MS = 250;
/** How many matching values a list draws at once; the search box reaches the rest. */
const SHOWN_VALUES = 300;

const sameSet = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((v) => b.includes(v));

type Loaded<T> = T | 'loading' | 'error';

@customElement('dashboard-slicer-tile')
export class DashboardSlicerTile extends LightElement {
  @property({ attribute: false }) tile!: DashboardTile;
  @property({ attribute: false }) shared!: SharedContext;
  @property({ type: Boolean }) editing = false;

  private store = new StoreController(this, [slicerSelections, slicerNotes, dashboards]);

  /** Unapplied ticks for a list or dropdown. Null means "as applied". */
  @state() private draft: string[] | null = null;
  @state() private search = '';
  @state() private options: Loaded<SlicerOptions> = 'loading';
  @state() private profile: Loaded<ColumnProfile> = 'loading';
  /** A list's values read in the dashboard's time; null: unknown, dim nothing. */
  @state() private inWindow: Set<string> | null = null;
  /** A range being dragged, as shares of the extent, before it is committed. */
  @state() private live: [number, number] | null = null;

  private loadedFor = '';
  private windowFor = '';

  override willUpdate(): void {
    this.load();
  }

  /* ── Loading ──────────────────────────────────────────── */

  private load(): void {
    const def = this.tile?.slicer;
    const ships = this.shared?.ships ?? [];
    if (!def || !ships.length) return;
    const discrete = def.mode === 'list' || def.mode === 'dropdown';
    const want = [def.column, def.mode, ships.join(','), discrete ? '' : `${this.shared.start}|${this.shared.end}`].join('~');
    if (discrete) this.loadWindowValues(def.column, ships);
    if (want === this.loadedFor) return;
    this.loadedFor = want;
    if (discrete) {
      this.options = 'loading';
      slicerOptions(ships, def.column).then(
        (o) => this.loadedFor === want && (this.options = o),
        () => this.loadedFor === want && (this.options = 'error'),
      );
    } else {
      this.profile = 'loading';
      slicerProfile(ships, def.column, this.shared.start, this.shared.end).then(
        (p) => this.loadedFor === want && (this.profile = p),
        () => this.loadedFor === want && (this.profile = 'error'),
      );
    }
  }

  /** Which values the dashboard's time holds, to dim the rest (`slicerWindowValues`). */
  private loadWindowValues(column: string, ships: string[]): void {
    const { start, end } = this.shared;
    const want = [column, ships.join(','), start, end].join('~');
    if (want === this.windowFor) return;
    this.windowFor = want;
    this.inWindow = null;
    slicerWindowValues(ships, column, start, end).then(
      (set) => this.windowFor === want && (this.inWindow = set),
      () => undefined,
    );
  }

  /* ── Selection ────────────────────────────────────────── */

  private get dashboardId(): string {
    return activeDashboard()?.id ?? '';
  }

  private applied(): SlicerSelection {
    return selectionOf(this.dashboardId, this.tile.id);
  }

  private pickedValues(): string[] {
    return this.draft ?? this.applied().values ?? [];
  }

  private dirty(): boolean {
    return this.draft !== null && !sameSet(this.draft, this.applied().values ?? []);
  }

  /** Dashboard setting **Filter while dragging**: picks apply as they are made. */
  private get liveApply(): boolean {
    return !!activeDashboard()?.settings.liveSlicers;
  }

  private toggle(value: string): void {
    const now = this.pickedValues();
    this.draft = now.includes(value) ? now.filter((v) => v !== value) : [...now, value];
    if (this.liveApply) this.commitValues();
  }

  private commitValues(): void {
    applySelection(this.dashboardId, this.tile.id, { values: this.pickedValues() });
    this.draft = null;
  }

  private clear(): void {
    applySelection(this.dashboardId, this.tile.id, null);
    this.draft = null;
    this.live = null;
  }

  private emitEdit(): void {
    this.dispatchEvent(new CustomEvent('slicer-edit', { bubbles: true, detail: { id: this.tile.id } }));
  }

  private emitRemove(): void {
    this.dispatchEvent(
      new CustomEvent('tile-remove', { bubbles: true, detail: { id: this.tile.id } }),
    );
  }

  /* ── Range ────────────────────────────────────────────── */

  /** The window as shares of the extent: applied ends where set, else the whole. */
  private shares(p: ColumnProfile): [number, number] {
    if (this.live) return this.live;
    const sel = this.applied();
    const span = (p.max ?? 0) - (p.min ?? 0) || 1;
    const a = sel.min == null ? 0 : (sel.min - (p.min ?? 0)) / span;
    const b = sel.max == null ? 1 : (sel.max - (p.min ?? 0)) / span;
    return [Math.min(Math.max(a, 0), 1), Math.min(Math.max(b, 0), 1)];
  }

  private commitRange(p: ColumnProfile, a: number, b: number): void {
    const span = (p.max ?? 0) - (p.min ?? 0);
    // Ends at the data's own bounds are stored open, so an untouched side stays
    // "everything" even when a longer window brings in wider data.
    applySelection(this.dashboardId, this.tile.id, {
      min: a <= 0.0005 ? null : (p.min ?? 0) + a * span,
      max: b >= 0.9995 ? null : (p.min ?? 0) + b * span,
    });
  }

  private onTrackDown(e: PointerEvent, p: ColumnProfile): void {
    if (e.button !== 0) return;
    const track = e.currentTarget as HTMLElement;
    const target = e.target as HTMLElement;
    const mode = target.classList.contains('is-lo')
      ? 'lo'
      : target.classList.contains('is-hi')
        ? 'hi'
        : target.classList.contains('slicer-band')
          ? 'pan'
          : 'jump';
    e.preventDefault();
    e.stopPropagation();
    const rect = track.getBoundingClientRect();
    const along = (ev: PointerEvent): number =>
      Math.min(1, Math.max(0, (ev.clientX - rect.left) / (rect.width || 1)));
    const [a0, b0] = this.shares(p);
    const start = along(e);
    try {
      track.setPointerCapture(e.pointerId);
    } catch {
      // A synthetic or already-ended pointer - the listeners below still work.
    }
    track.classList.add('is-dragging');
    const window = (ev: PointerEvent): [number, number] => {
      const q = along(ev);
      if (mode === 'lo') return [Math.min(q, b0 - MIN_SHARE), b0];
      if (mode === 'hi') return [a0, Math.max(q, a0 + MIN_SHARE)];
      if (mode === 'pan') {
        const d = Math.min(1 - b0, Math.max(-a0, q - start));
        return [a0 + d, b0 + d];
      }
      // Pressed on the bare track: move the nearer end there.
      return Math.abs(q - a0) < Math.abs(q - b0)
        ? [Math.min(q, b0 - MIN_SHARE), b0]
        : [a0, Math.max(q, a0 + MIN_SHARE)];
    };
    // Filter while dragging (a dashboard setting, off by default): commit a few
    // times a second as the handles move. Each commit refetches the tab's charts;
    // a tile drops a response that a later commit has overtaken.
    let lastCommit = 0;
    const move = (ev: PointerEvent): void => {
      this.live = window(ev);
      if (this.liveApply && performance.now() - lastCommit >= LIVE_COMMIT_MS) {
        lastCommit = performance.now();
        this.commitRange(p, this.live[0], this.live[1]);
      }
    };
    const up = (ev: PointerEvent): void => {
      if (track.hasPointerCapture(ev.pointerId)) track.releasePointerCapture(ev.pointerId);
      track.classList.remove('is-dragging');
      track.removeEventListener('pointermove', move);
      track.removeEventListener('pointerup', up);
      track.removeEventListener('pointercancel', up);
      const [a, b] = window(ev);
      this.live = null;
      // A commit on release, always: the charts end on exactly where it was let go.
      this.commitRange(p, a, b);
    };
    track.addEventListener('pointermove', move);
    track.addEventListener('pointerup', up);
    track.addEventListener('pointercancel', up);
    if (mode === 'jump') move(e);
  }

  private fmt(p: ColumnProfile, v: number): string {
    return p.temporal ? fmtTimeShort(v) : formatNum(v);
  }

  /* ── Rendering: bodies ────────────────────────────────── */

  private note(message: string | TemplateResult): TemplateResult {
    return html`<span class="slicer-wait">${message}</span>`;
  }

  /** The values to tick - the list's body, and the dropdown's panel. */
  private renderValues(opts: SlicerOptions): TemplateResult {
    if (opts.kind === 'empty') return this.note('No values for these ships.');
    const needle = this.search.trim().toLowerCase();
    const matches = needle ? opts.values.filter((v) => v.toLowerCase().includes(needle)) : opts.values;
    const values = matches.slice(0, SHOWN_VALUES);
    const picked = this.pickedValues();
    const hidden = matches.length - values.length;
    return html`
      ${opts.values.length > SEARCH_ABOVE
        ? html`<input
            class="dash-slicer-search"
            type="text"
            autocomplete="off"
            placeholder=${`Search ${opts.values.length.toLocaleString()}${opts.truncated ? '+' : ''} values…`}
            aria-label="Search values"
            .value=${this.search}
            @input=${(e: Event) => {
              this.search = (e.target as HTMLInputElement).value;
            }}
          />`
        : nothing}
      <div class="dash-slicer-list" role="group" aria-label=${this.tile.title}>
        ${values.map(
          (v) => html`<label
            class="dash-slicer-value ${this.inWindow && !this.inWindow.has(v) ? 'is-absent' : ''}"
            title=${this.inWindow && !this.inWindow.has(v) ? `${v} - no readings in the dashboard's time` : v}
          >
            <input class="col-checkbox" type="checkbox" .checked=${picked.includes(v)} @change=${() => this.toggle(v)} />
            <span>${v}</span>
          </label>`,
        )}
        ${values.length === 0 ? this.note('Nothing matches.') : nothing}
        ${hidden > 0 || (opts.truncated && !needle)
          ? this.note(
              opts.truncated
                ? `Showing the first ${values.length.toLocaleString()} of more than ${opts.values.length.toLocaleString()} - search to find the rest.`
                : `${hidden.toLocaleString()} more - search to find them.`,
            )
          : nothing}
      </div>
      ${this.dirty()
        ? html`<div class="dash-slicer-foot">
            <button
              class="dash-btn is-primary"
              @click=${(e: Event) => {
                this.commitValues();
                // In a dropdown the panel has done its job.
                (e.currentTarget as HTMLElement).dispatchEvent(new CustomEvent('request-close', { bubbles: true }));
              }}
            >
              Apply
            </button>
            <button class="dash-btn" @click=${() => (this.draft = null)}>Undo</button>
          </div>`
        : nothing}
    `;
  }

  /** The dropdown in a dropdown filter: one line that opens the values. */
  private renderDropdown(opts: SlicerOptions): TemplateResult {
    const applied = this.applied().values ?? [];
    const label =
      applied.length === 0 ? 'All' : applied.length === 1 ? applied[0]! : `${applied[0]} +${applied.length - 1}`;
    return html`<dash-dropdown
      class="dash-slicer-select"
      label=${label}
      fixed
      .panel=${() => html`<div class="dash-slicer-panel">${this.renderValues(opts)}</div>`}
    ></dash-dropdown>`;
  }

  /** A range or dates filter: Explore's track and readout, without the name - the header has it. */
  private renderRange(p: ColumnProfile): TemplateResult {
    if (p.min == null || p.max == null || !p.counts.length) return this.note('No readings in this window.');
    const [a, b] = this.shares(p);
    const peak = Math.max(...p.counts, 1);
    const n = p.counts.length;
    // The readout says what is applied, even past the data's extent, where the
    // handles can only sit at the edge; while dragging, where the handles are.
    const sel = this.applied();
    const lo = this.live ? p.min + a * (p.max - p.min) : (sel.min ?? p.min);
    const hi = this.live ? p.min + b * (p.max - p.min) : (sel.max ?? p.max);
    const narrowed = narrows(sel) || !!this.live;
    return html`<div class="slicer-row dash-slicer-line ${narrowed ? 'is-narrowed' : ''}">
      <div
        class="slicer-track"
        title="Drag the ends to narrow, the middle to move - double-click to show everything"
        @pointerdown=${(e: PointerEvent) => this.onTrackDown(e, p)}
        @dblclick=${() => this.clear()}
      >
        <div class="slicer-hist" aria-hidden="true">
          ${p.counts.map((c, i) => {
            const inside = (i + 0.5) / n >= a && (i + 0.5) / n <= b;
            return html`<span class=${inside ? 'is-in' : ''} style=${`--fill:${Math.max(0.05, c / peak)}`}></span>`;
          })}
        </div>
        <div class="slicer-band" style=${`left:${a * 100}%;width:${(b - a) * 100}%`}>
          <span class="slicer-handle is-lo"></span>
          <span class="slicer-handle is-hi"></span>
        </div>
      </div>
      <span class="slicer-readout">${this.fmt(p, lo)} – ${this.fmt(p, hi)}</span>
    </div>`;
  }

  private renderBody(): TemplateResult {
    const def = this.tile.slicer!;
    if (!this.shared?.ships.length) return this.note('Pick a ship in the toolbar.');
    const discrete = def.mode === 'list' || def.mode === 'dropdown';
    const data = discrete ? this.options : this.profile;
    if (data === 'loading') return this.note(html`<span class="spinner"></span> Loading`);
    if (data === 'error') return this.note('Could not load this column.');
    if (def.mode === 'list') return this.renderValues(data as SlicerOptions);
    if (def.mode === 'dropdown') return this.renderDropdown(data as SlicerOptions);
    return this.renderRange(data as ColumnProfile);
  }

  /** Why the selection changed by itself: the dashboard's time moved under it. */
  private renderNote(): TemplateResult | typeof nothing {
    const note = slicerNoteOf(this.dashboardId, this.tile.id);
    if (!note) return nothing;
    return html`<div class="dash-slicer-note" role="status" title=${note}>
      <span>${note}</span>
      <button
        class="dash-slicer-note-close"
        aria-label="Close note"
        @click=${() => dismissSlicerNote(this.dashboardId, this.tile.id)}
      >
        ×
      </button>
    </div>`;
  }

  /** The chart tile's header: the label, then Clear, and Edit / Remove in Edit mode. */
  private renderHead(narrowed: boolean): TemplateResult {
    const def = this.tile.slicer!;
    return html`<div class="dash-tile-head" data-drag-handle>
      <div class="dash-tile-heading">
        <span class="dash-tile-title" title=${`${this.tile.title} - filters ${def.column}`}>
          ${this.tile.title || def.column}
        </span>
      </div>
      <div class="dash-tile-actions">
        ${narrowed
          ? html`<button class="dash-tile-btn" aria-label="Clear filter - show everything again" @click=${() => this.clear()}>
              ⟲
            </button>`
          : nothing}
        ${this.editing
          ? html`<button class="dash-tile-btn" aria-label="Edit filter" @click=${() => this.emitEdit()}>
                ✎
              </button>
              <button class="dash-tile-btn is-danger" aria-label="Remove filter" @click=${() => this.emitRemove()}>
                ✕
              </button>`
          : nothing}
      </div>
    </div>`;
  }

  override render(): TemplateResult {
    void this.store;
    const def = this.tile?.slicer;
    if (!def) return html``;
    const narrowed = narrows(this.applied());
    return html`<div class="dash-tile is-slicer is-${def.mode} ${narrowed ? 'is-narrowed' : ''}">
      ${this.renderHead(narrowed)}
      <div class="dash-slicer-body">${this.renderBody()}</div>
      ${this.renderNote()}
      ${this.editing ? html`<div class="dash-tile-resize" data-resize-handle></div>` : nothing}
    </div>`;
  }

  /** Close an open dropdown when the tile is taken away. */
  override disconnectedCallback(): void {
    this.querySelector<DashDropdown>('dash-dropdown')?.close();
    super.disconnectedCallback();
  }
}
